import React, { useRef, useState, useEffect } from "react";
import { Row, Col, Container, Button, Nav, Form, Card } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  getPatientById,
  getAppointmentById,
  getServiceById,
  fetchSessionByAppointmentId,
  getSessionById,
} from "../../api";
import { FollowUpSession } from "./FollowUpSession";
import { UpdateSession } from "./EditSession";
import { PrevSessions } from "../Sessions/PrevSessions";
import { PatientReports } from "../PatientReports/patientReports";
import { LinkNotes } from "./LinkNotes";
import PatientFileManager from "./PatientFiles";
import Diagnosis from "./Diagnosis";

export default function Session({ extraMobileMenu = [], onMobileNavigate }) {
  const followUpRef = useRef();
  const { id, appointmentId, sessionId } = useParams();
  const hasAppointment = appointmentId && appointmentId !== "1";
  const editingAdHoc = !!sessionId && !hasAppointment;

  // sub-sections: apptTemplate | apptNotepad | apptMedia
  const [activeSection, setActiveSection] = useState(
    appointmentId === "1" ? "historical" : "apptTemplate",
  );

  const [patient, setPatient] = useState();
  const [appointment, setAppointment] = useState();
  const [service, setService] = useState();
  const [existingSessionId, setExistingSessionId] = useState(null);
  const [existingSession, setExistingSession] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 1024);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  const isApptSubtab = ["apptTemplate", "apptNotepad", "apptMedia"].includes(
    activeSection,
  );

  const formattedDateTime =
    appointment?.start && !isNaN(new Date(appointment.start))
      ? new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(appointment.start))
      : "Invalid Date";

  const NotesHeader = ({ isAdHoc }) => {
    return (
      <div>
        <Row className="align-items-center">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              {isAdHoc ? (
                <p className="text-primary fw-bold mb-0"></p>
              ) : (
                <p className="text-primary fw-bold mb-0">
                  {formattedDateTime} &nbsp;-&nbsp;{" "}
                  {service?.servicesName || "Loading service name..."}
                </p>
              )}
            </div>
          </Col>
        </Row>

      </div>
    );
  };

  const renderAppointmentNotes = () => {
    // --- EDITING AD-HOC (by sessionId) ---
    if (editingAdHoc) {
      return (
        <>
          <NotesHeader isAdHoc />
          {existingSessionId ? (
            <UpdateSession
              key={`edit-adhoc-${existingSessionId}`}
              sessionId={existingSession._id}
              appointmentId={existingSession.sessionsAppointmentId || null} // null for ad-hoc
              practiceId={existingSession.sessionsPracticeId}
              patientId={id}
              sections={existingSession.sections}
              notepadContentR={existingSession.notepadContent || ""}
              notepadContentType={existingSession.notepadContentType || ""}
              mediaFilesR={existingSession.mediaFiles || []}
              appointmentStatus={existingSession.appointmentStatus || null}
              serviceName={existingSession.sessionsServiceName || "Ad Hoc"}
              appointmentStart={
                existingSession.sessionsAppointmentStart ||
                existingSession.createdAt
              }
              templateId={existingSession.sessionsTemplateId}
              resourceId={existingSession.sessionsResourceId || null}
              activeSection={activeSection}
              onSaved={() => setActiveSection("historical")}
              capturedValuesR={existingSession.capturedValues || {}}
              updatedAtR={existingSession.updatedAt || null}
            />
          ) : (
            <p>Loading session…</p>
          )}
        </>
      );
    }

    // --- AD-HOC CREATE (no appointment, no sessionId) ---
    if (!hasAppointment) {
      return (
        <>
          <NotesHeader isAdHoc />
          <FollowUpSession
            key={`adhoc-${id}`}
            ref={followUpRef}
            adHoc
            appointmentId={null}
            patientId={id}
            serviceName={""}
            resourceId={null}
            appointmentStart={null}
            appointmentStatus={null}
            practiceId={undefined}
            activeSection={activeSection}
            onUpdate={() => setActiveSection("historical")}
          />
        </>
      );
    }

    // --- APPOINTMENT BRANCH (unchanged) ---
    if (!appointment) return <p>Loading appointment details...</p>;

    const upKey = `up-${existingSessionId || "none"}`;
    const createKey = `fu-${appointmentId}`;

    if (existingSessionId) {
      return (
        <>
          <NotesHeader isAdHoc={false} />
          <UpdateSession
            key={upKey}
            sessionId={existingSession._id}
            appointmentId={existingSession.sessionsAppointmentId}
            practiceId={existingSession.sessionsPracticeId}
            patientId={id}
            sections={existingSession.sections}
            notepadContentR={existingSession.notepadContent || ""}
            notepadContentType={existingSession.notepadContentType || ""}
            mediaFilesR={existingSession.mediaFiles || []}
            appointmentStatus={appointment.status}
            serviceName={service?.servicesName || ""}
            appointmentStart={appointment.start}
            templateId={existingSession.sessionsTemplateId}
            resourceId={appointment.resourceId}
            activeSection={activeSection}
            onSaved={() => setActiveSection("historical")}
            capturedValuesR={existingSession.capturedValues || {}}
            updatedAtR={existingSession.updatedAt || null}
          />
        </>
      );
    }

    return (
      <>
        <NotesHeader isAdHoc={false} />
        <FollowUpSession
          key={createKey}
          ref={followUpRef}
          appointmentId={appointmentId}
          patientId={id}
          serviceName={service?.servicesName || ""}
          resourceId={appointment.resourceId}
          appointmentStart={appointment.start}
          appointmentStatus={appointment.status}
          practiceId={appointment.practiceId}
          activeSection={activeSection}
          onUpdate={() => setActiveSection("historical")}
        />
      </>
    );
  };

  const doAndCollapse = (fn, payload) => {
    try {
      fn?.();
    } finally {
      setIsSidebarCollapsed(true);
      onMobileNavigate?.(payload);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsNarrow(window.innerWidth <= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function loadPatientAndAppointment() {
      try {
        if (!appointmentId) return;

        // fetch patient when missing (undefined or null)
        if (!patient) {
          const patientData = await getPatientById(id);
          setPatient(patientData);
        }

        const appointmentData = await getAppointmentById(appointmentId);
        setAppointment(appointmentData);
        setSelectedStatus(appointmentData.status);

        if (appointmentData.serviceId) {
          const serviceData = await getServiceById(appointmentData.serviceId);
          setService(serviceData);
        }

        // ensure correct default tab
        setActiveSection(appointmentId === "1" ? "historical" : "apptTemplate");
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadPatientAndAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appointmentId]);

  useEffect(() => {
    async function loadSession() {
      try {
        if (editingAdHoc && sessionId) {
          const s = await getSessionById(sessionId); // <- implement in ../../api if needed
          if (s && s._id) {
            setExistingSessionId(s._id);
            setExistingSession(s);
          }
          setIsLoading(false);
          return;
        }
        // existing appointment-based lookup
        if (appointmentId) {
          const s = await fetchSessionByAppointmentId(appointmentId);
          if (s && s._id) {
            setExistingSessionId(s._id);
            setExistingSession(s);
          }
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadSession();
  }, [appointmentId, sessionId, editingAdHoc]);

  useEffect(() => {
    if (appointment) setSelectedStatus(appointment.status);
  }, [appointment]);

  useEffect(() => {
    if (!isMobile && activeSection === "apptMedia") {
      setActiveSection("apptTemplate");
    }
  }, [isMobile, activeSection]);

  if (isLoading) return <p>Loading session data...</p>;

  // ------- Sidebar items (add Link Notes after Previous Notes) -------
  const appointmentKeys = isMobile
    ? ["apptTemplate", "apptNotepad", "apptMedia"]
    : ["apptTemplate", "apptNotepad"];

  const parentIsActive = appointmentKeys.includes(activeSection);

  const sidebarItems = [
    {
      key: "appointment",
      label: hasAppointment ? "Appointment Notes" : "Add Note",
      icon: "fa-light fa-note-medical",
    },
    {
      key: "historical",
      label: "Previous Notes",
      icon: "fa-sharp fa-light fa-notes",
    },
    {
      key: "diagnosis",
      label: "Diagnoses",
      icon: "fa-sharp fa-light fa-user-doctor-message",
    },
    {
      key: "linkNotes",
      label: "Link Notes",
      icon: "fa-light fa-paperclip",
    },
    {
      key: "files",
      label: "Patient Files",
      icon: "fa-light fa-cabinet-filing",
    },
    {
      key: "reports",
      label: "Generate Reports",
      icon: "fa-light fa-file-arrow-down",
    },
  ];

  const titleMap = {
    apptTemplate: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    apptNotepad: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    apptMedia: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    historical: "PREVIOUS SESSIONS",
    diagnosis: "DIAGNOSES",
    linkNotes: "LINK NOTES",
    files: "PATIENT FILES",
    reports: "GENERATE REPORTS",
  };

  const renderContent = () => {
    if (["apptTemplate", "apptNotepad", "apptMedia"].includes(activeSection)) {
      return renderAppointmentNotes();
    }
    if (activeSection === "historical") return <PrevSessions patientId={id} />;

    if (activeSection === "linkNotes") {
      return (
        <LinkNotes
          patientId={id}
          patientFirstName={appointment?.patientFirstName}
          patientLastName={appointment?.patientLastName}
          onSuccess={() => setActiveSection("historical")}
        />
      );
    }

    if (activeSection === "files") return <PatientFileManager patientId={id} />;
    if (activeSection === "reports") return <PatientReports patientId={id} />;
    if (activeSection === "diagnosis") {
      return <Diagnosis patientId={id} />;
    }

    return null;
  };

  return (
    <div className="ms-1">
      {/* ── MOBILE: collapsible sidebar drawer ── */}
      {isMobile && (
        <>
          {/* Arrow toggle */}
          <div
            className="position-fixed d-flex align-items-center justify-content-center bg-primary text-white shadow-lg"
            style={{
              left: isSidebarCollapsed ? "10px" : "250px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "20px",
              height: "40px",
              borderRadius: "0 8px 8px 0",
              cursor: "pointer",
              transition: "left 0.3s ease",
              zIndex: 1050,
            }}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            <i
              className={`bi ${isSidebarCollapsed ? "bi-chevron-right" : "bi-chevron-left"}`}
              style={{ fontSize: "18px" }}
            />
          </div>

          {/* Overlay */}
          {!isSidebarCollapsed && (
            <div
              className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
              style={{ top: 0, left: 0, zIndex: 1040 }}
              onClick={() => setIsSidebarCollapsed(true)}
            />
          )}

          {/* Drawer */}
          <div
            className="position-fixed bg-white shadow-lg d-flex flex-column"
            style={{
              left: isSidebarCollapsed ? "-250px" : "0",
              top: "0",
              width: "250px",
              height: "100vh",
              transition: "left 0.3s ease",
              zIndex: 1045,
            }}
          >
            <div className="p-3 border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold text-primary">Menu</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsSidebarCollapsed(true)}
                >
                  ×
                </Button>
              </div>
            </div>

            <div className="flex-grow-1 overflow-auto">
              {extraMobileMenu.length > 0 && (
                <>
                  <div className="px-3 pt-3 text-muted small">Patient</div>
                  <div className="p-3">
                    {extraMobileMenu.map((item) => (
                      <div key={item.key} className="mb-2">
                        <Button
                          variant="outline-primary"
                          className="w-100 text-start"
                          onClick={() => doAndCollapse(item.onClick, "patient")}
                        >
                          {item.label}
                        </Button>
                      </div>
                    ))}
                  </div>
                  <hr className="my-0" />
                </>
              )}

              <div className="p-3 pt-3">
                <div className="text-muted small mb-2">Session</div>
                {sidebarItems.map((item) => {
                  const isActive =
                    item.key === "appointment"
                      ? parentIsActive
                      : activeSection === item.key;
                  return (
                    <div key={item.key} className="mb-2">
                      <Button
                        variant={isActive ? "primary" : "outline-primary"}
                        className="w-100 text-start d-flex align-items-center gap-2"
                        onClick={() => {
                          setActiveSection(
                            item.key === "appointment" ? "apptTemplate" : item.key,
                          );
                          doAndCollapse(null, "session");
                        }}
                      >
                        <i className={item.icon} />
                        <span>{item.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── DESKTOP: centered top row of buttons with title pinned left ── */}
      {!isMobile && (
        <div className="d-flex align-items-start mt-2 pb-2">
          {/* Left: title — takes equal share so center is truly centred */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
            <h5 className="text-primary mt-3 mb-0">{titleMap[activeSection] || ""}</h5>
          </div>
          {/* Centre: buttons */}
          <div className="d-flex justify-content-center flex-wrap gap-1 mt-3" style={{ flex: "0 1 auto" }}>
            {sidebarItems.map((item, idx) => (
              <React.Fragment key={item.key}>
                {idx === 3 && isNarrow && <div style={{ width: "100%" }} />}
                <Button
                  variant={
                    (item.key === "appointment" ? parentIsActive : activeSection === item.key)
                      ? "primary"
                      : "outline-primary"
                  }
                  className="rounded-0"
                  style={{ minWidth: "120px" }}
                  onClick={() =>
                    setActiveSection(
                      item.key === "appointment" ? "apptTemplate" : item.key,
                    )
                  }
                >
                  <i className={`${item.icon} me-2`} />
                  {item.label}
                </Button>
              </React.Fragment>
            ))}
          </div>
          {/* Right: spacer matching left so buttons stay centred */}
          <div style={{ flex: "1 1 0" }} />
        </div>
      )}

      {/* Content area */}
      <div className="pt-1">
        {renderContent()}
      </div>
    </div>
  );
}
